import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  TextField,
  Toggle,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  Separator,
  Label,
  Checkbox,
  SpinnerSize,
  Spinner,
  Panel,
  PanelType
} from '@fluentui/react';
import {
  DocumentRegular,
  ListRegular,
  SearchRegular,
  ShieldRegular,
  ClockRegular,
  DatabaseRegular,
  EyeRegular,
  InfoRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './SharePointConnectorSetup.module.css';

interface SharePointOnPremConnectorSetupProps {
  isVisible: boolean;
  onDismiss: () => void;
  onComplete: (config: SharePointOnPremConfig) => void;
  editingJob?: SharePointOnPremConfig; // Optional prop for editing existing jobs
}

interface ValidationErrors {
  name?: string;
  sourceUrls?: string;
  username?: string;
  password?: string;
  domain?: string;
  storageContainer?: string;
  containerName?: string;
  selectedIndexName?: string;
  newIndexName?: string;
  selectedIndexMode?: string;
}

interface FileNameFilter {
  pattern: string;
  enabled: boolean;
}

interface SharePointOnPremConfig {
  name: string;
  description: string;
  sourceUrls: string[]; // Array for multiple URLs - first one is primary
  libraries: string[];
  crawlSubsites: boolean;
  contentTypes: string[];
  fileTypes: string[];
  aclEnabled: boolean;
  crawlMode: 'full' | 'delta';
  enableVectorization: boolean;
  enableEmbeddings: boolean;
  // File age filtering
  maxFileAgeEnabled: boolean;
  maxFileAgeDays: number;
  // Processing mode
  processingMode: 'realtime' | 'batch';
  uploadOnly: boolean; // Upload-only mode - skip indexer creation
  storageContainer: string;
  containerName: string; // Container management
  containerMode: 'existing' | 'new'; // Container selection mode
  // Index selection properties
  selectedIndexMode: 'existing' | 'new';
  selectedIndexName?: string;
  newIndexName?: string;
  // Authentication properties for on-premises
  username: string;
  password: string;
  domain: string;
  // Advanced filtering options
  advancedFiltering: {
    enabled: boolean;
    filenameFilters: {
      contains: string[];
      patterns: string[];
      startsWidth: string[];
      exact: string[];
    };
    urlContentFilters: {
      mustContain: string[];
      cannotContain: string[];
    };
    regexPatterns: {
      include: string[];
      exclude: string[];
    };
    depthLimit: number;
    maxFileSize: number; // in MB
    maxItems: number; // Maximum number of items to process (0 = no limit)
  };
  // Legacy filename filtering (backward compatibility)
  filenameFilters: FileNameFilter[];
  filenameFilterLogic: 'AND' | 'OR';
  // Library filtering
  includedLibraries: string[];
  excludedLibraries: string[];
  // Scheduling properties
  schedulingEnabled?: boolean;
  crawlFrequency?: string;
  crawlTime?: string;
  incrementalCrawl?: boolean;
  // Optional properties for job results
  indexName?: string;
  jobId?: string;
  status?: string;
}

interface SearchIndex {
  id: string;
  name: string;
  connectorType: string;
  vectorEnabled: boolean;
  semanticEnabled: boolean;
  status: string;
  documentCount: number;
}

interface DiscoveryResult {
  totalSites: number;
  totalSubsites: number;
  estimatedDocuments: number;
  siteStructure: SiteInfo[];
  librariesFound: string[];
  fileTypeBreakdown: { [key: string]: number };
  contentTypeBreakdown: { [key: string]: number };
  discoveryTime: string;
}

interface SiteInfo {
  name: string;
  url: string;
  documentCount: number;
  subsites: SiteInfo[];
}

const SharePointOnPremConnectorSetup: React.FC<SharePointOnPremConnectorSetupProps> = ({
  isVisible,
  onDismiss,
  onComplete,
  editingJob
}) => {
  // Step navigation state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;

  // Configuration state
  const [loading, setLoading] = useState(false);
  
  // Form validation states
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  
  // Container management states
  const [availableContainers, setAvailableContainers] = useState<string[]>([]);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [containerMode, setContainerMode] = useState<'existing' | 'new'>('new');
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
  const [discoveryResult, setDiscoveryResult] = useState<DiscoveryResult | null>(null);

  // Index management state
  const [availableIndexes, setAvailableIndexes] = useState<SearchIndex[]>([]);
  const [loadingIndexes, setLoadingIndexes] = useState(false);
  const [creatingIndex, setCreatingIndex] = useState(false);

  // Step navigation functions
  const handleNextStep = () => {
    setIsSubmitAttempted(true);
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      setIsSubmitAttempted(false);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setIsSubmitAttempted(false);
  };

  const validateCurrentStep = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    switch (currentStep) {
      case 1: // Basic Configuration
        if (!config.name.trim()) {
          errors.name = 'Connector name is required';
          isValid = false;
        }
        if (config.sourceUrls.length === 0 || config.sourceUrls.every(url => !url.trim())) {
          errors.sourceUrls = 'At least one SharePoint site URL is required';
          isValid = false;
        }
        if (!config.username.trim()) {
          errors.username = 'Username is required';
          isValid = false;
        }
        if (!config.password.trim()) {
          errors.password = 'Password is required';
          isValid = false;
        }
        break;

      case 2: // Index Selection
        if (config.selectedIndexMode === 'existing' && !config.selectedIndexName) {
          errors.selectedIndexName = 'Please select an existing index';
          isValid = false;
        }
        if (config.selectedIndexMode === 'new' && !config.newIndexName?.trim()) {
          errors.newIndexName = 'Please enter a name for the new index';
          isValid = false;
        }
        break;

      case 6: // Scheduling & Processing
        if (config.processingMode === 'batch' && !config.storageContainer.trim()) {
          errors.storageContainer = 'Storage container is required for batch processing';
          isValid = false;
        }
        break;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const [config, setConfig] = useState({
    siteUrl: '',
    username: '',
    password: '',
    domain: '',
    scheduleType: 'once',
    indexName: '',
    // SharePoint metadata properties
    name: '',
    description: '',
    sourceUrls: [''] as string[], // Start with one empty URL
    libraries: [] as string[],
    crawlSubsites: true, // Default to true as requested
    contentTypes: ['documents', 'lists', 'pages', 'news', 'events'] as string[], // Default to all content types
    fileTypes: ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'html', 'msg'] as string[], // Default to all file types
    aclEnabled: true, // Default to true for security
    crawlMode: 'full' as 'full' | 'delta',
    enableVectorization: true,
    enableEmbeddings: true,
    // File age filtering - default to 90 days
    maxFileAgeEnabled: false,
    maxFileAgeDays: 90,
    // Processing mode
    processingMode: 'realtime' as 'realtime' | 'batch',
    uploadOnly: false, // Default to full processing mode
    storageContainer: '',
    containerName: '', // Container management
    containerMode: 'existing' as 'existing' | 'new', // Container selection mode
    selectedIndexMode: 'existing' as 'existing' | 'new',
    selectedIndexName: '',
    newIndexName: '',
    // Filename filtering (like public web connector)
    filenameFilters: [] as FileNameFilter[],
    filenameFilterLogic: 'OR' as 'AND' | 'OR',
    // Advanced filtering options
    advancedFiltering: {
      enabled: false,
      filenameFilters: {
        contains: [] as string[],
        patterns: [] as string[],
        startsWidth: [] as string[],
        exact: [] as string[]
      },
      urlContentFilters: {
        mustContain: [] as string[],
        cannotContain: [] as string[]
      },
      regexPatterns: {
        include: [] as string[],
        exclude: [] as string[]
      },
      depthLimit: 10,
      maxFileSize: 50,
      maxItems: 0 // No limit by default
    },
    // Library filtering
    includedLibraries: [] as string[],
    excludedLibraries: [] as string[],
    // Scheduling properties
    schedulingEnabled: true,
    crawlFrequency: 'daily',
    crawlTime: '02:00',
    incrementalCrawl: true
  });

  // Helper functions for managing URLs
  const addUrl = () => {
    setConfig(prev => ({
      ...prev,
      sourceUrls: [...prev.sourceUrls, '']
    }));
  };

  const removeUrl = (index: number) => {
    setConfig(prev => ({
      ...prev,
      sourceUrls: prev.sourceUrls.filter((_, i) => i !== index)
    }));
  };

  const updateUrl = (index: number, newUrl: string) => {
    setConfig(prev => ({
      ...prev,
      sourceUrls: prev.sourceUrls.map((url, i) => i === index ? newUrl : url)
    }));
  };

  const contentTypeOptions: IDropdownOption[] = [
    { key: 'documents', text: 'Document Libraries', selected: true },
    { key: 'lists', text: 'SharePoint Lists', selected: true },
    { key: 'pages', text: 'Site Pages' },
    { key: 'news', text: 'News Articles' },
    { key: 'events', text: 'Calendar Events' }
  ];

  const fileTypeOptions: IDropdownOption[] = [
    { key: 'pdf', text: 'PDF (.pdf)', selected: true },
    { key: 'docx', text: 'Word Documents (.docx)', selected: true },
    { key: 'doc', text: 'Word Documents (.doc)', selected: true },
    { key: 'xlsx', text: 'Excel Spreadsheets (.xlsx)', selected: true },
    { key: 'pptx', text: 'PowerPoint Presentations (.pptx)', selected: true },
    { key: 'txt', text: 'Text Files (.txt)', selected: true },
    { key: 'html', text: 'HTML Files (.html)' },
    { key: 'msg', text: 'Outlook Messages (.msg)' }
  ];

  const crawlModeOptions: IDropdownOption[] = [
    { key: 'full', text: 'Full Crawl - Index all content' },
    { key: 'delta', text: 'Delta Crawl - Only changed content' }
  ];

  // Load available indexes when component mounts
  React.useEffect(() => {
    if (isVisible) {
      loadAvailableIndexes();
      loadAvailableContainers();
    }
  }, [isVisible]);

  // Initialize form when editing an existing job
  React.useEffect(() => {
    if (editingJob) {
      console.log('🔧 Initializing form with editingJob:', editingJob);
      setConfig({
        ...config,
        name: editingJob.name || '',
        description: editingJob.description || '',
        sourceUrls: editingJob.sourceUrls || [''],
        libraries: editingJob.libraries || [],
        crawlSubsites: editingJob.crawlSubsites !== undefined ? editingJob.crawlSubsites : true,
        contentTypes: editingJob.contentTypes || ['documents', 'lists', 'pages', 'news', 'events'],
        fileTypes: editingJob.fileTypes || ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'html', 'msg'],
        aclEnabled: editingJob.aclEnabled !== undefined ? editingJob.aclEnabled : true,
        crawlMode: editingJob.crawlMode || 'full',
        enableVectorization: editingJob.enableVectorization !== undefined ? editingJob.enableVectorization : true,
        enableEmbeddings: editingJob.enableEmbeddings !== undefined ? editingJob.enableEmbeddings : true,
        maxFileAgeEnabled: editingJob.maxFileAgeEnabled || false,
        maxFileAgeDays: editingJob.maxFileAgeDays || 90,
        processingMode: editingJob.processingMode || 'realtime',
        uploadOnly: editingJob.uploadOnly !== undefined ? editingJob.uploadOnly : false,
        storageContainer: editingJob.storageContainer || '',
        containerName: editingJob.containerName || '',
        containerMode: editingJob.containerMode || 'existing',
        selectedIndexMode: editingJob.selectedIndexMode || (editingJob.indexName ? 'existing' : 'new'),
        selectedIndexName: editingJob.indexName || editingJob.selectedIndexName || '',
        newIndexName: editingJob.newIndexName || '',
        username: editingJob.username || '',
        // DO NOT populate password from stored job for security
        password: '',
        domain: editingJob.domain || '',
        filenameFilters: editingJob.filenameFilters || [],
        filenameFilterLogic: editingJob.filenameFilterLogic || 'OR',
        advancedFiltering: editingJob.advancedFiltering || {
          enabled: false,
          filenameFilters: {
            contains: [],
            patterns: [],
            startsWidth: [],
            exact: []
          },
          urlContentFilters: {
            mustContain: [],
            cannotContain: []
          },
          regexPatterns: {
            include: [],
            exclude: []
          },
          depthLimit: 10,
          maxFileSize: 50,
          maxItems: 0 // No limit by default
        },
        includedLibraries: editingJob.includedLibraries || [],
        excludedLibraries: editingJob.excludedLibraries || [],
        schedulingEnabled: editingJob.schedulingEnabled !== undefined ? editingJob.schedulingEnabled : true,
        crawlFrequency: editingJob.crawlFrequency || 'daily',
        crawlTime: editingJob.crawlTime || '02:00',
        incrementalCrawl: editingJob.incrementalCrawl !== undefined ? editingJob.incrementalCrawl : true
      });
    }
  }, [editingJob]);

  // Validation functions for each step
  const validateStep = (step: number): ValidationErrors => {
    const errors: ValidationErrors = {};
    
    switch (step) {
      case 1:
        if (!config.name?.trim()) {
          errors.name = 'Job name is required';
        }
        if (!config.sourceUrls.length || !config.sourceUrls[0]?.trim()) {
          errors.sourceUrls = 'At least one source URL is required';
        } else {
          // Basic URL validation
          const urlPattern = /^https?:\/\/.+/i;
          const invalidUrls = config.sourceUrls.filter(url => url.trim() && !urlPattern.test(url.trim()));
          if (invalidUrls.length > 0) {
            errors.sourceUrls = 'Please enter valid URLs starting with http:// or https://';
          }
        }
        break;
      
      case 2:
        if (config.selectedIndexMode === 'existing') {
          if (!config.selectedIndexName?.trim()) {
            errors.selectedIndexName = 'Please select an existing index';
          }
        } else if (config.selectedIndexMode === 'new') {
          if (!config.newIndexName?.trim()) {
            errors.newIndexName = 'Index name is required';
          }
        } else {
          errors.selectedIndexMode = 'Please select an index mode';
        }
        break;
      
      case 3:
        if (!config.username?.trim()) {
          errors.username = 'Username is required';
        }
        // For editing jobs, password is only required if it's currently empty
        if (!config.password?.trim() && !editingJob) {
          errors.password = 'Password is required';
        }
        // Domain no longer required; only validate if supplied but blank (edge case) -> skip requirement
        // if (!config.domain?.trim()) {
        //   errors.domain = 'Domain is required';
        // }
        break;
      
      case 4:
        // Validate storage container only for batch processing mode (but not upload-only)
        if (config.processingMode === 'batch' && !config.uploadOnly) {
          if (config.containerMode === 'existing') {
            if (!config.containerName?.trim()) {
              errors.containerName = 'Please select an existing container for batch processing';
            }
          } else if (config.containerMode === 'new') {
            if (!config.containerName?.trim()) {
              errors.containerName = 'Container name is required for batch processing';
            }
          } else if (!config.storageContainer?.trim()) {
            errors.storageContainer = 'Storage container is required for batch processing';
          }
        }
        break;
      
      default:
        break;
    }
    
    return errors;
  };

  const isStepValid = (step?: number) => {
    const currentStepToCheck = step ?? currentStep;
    const errors = validateStep(currentStepToCheck);
    return Object.keys(errors).length === 0;
  };

  const loadAvailableIndexes = async () => {
    setLoadingIndexes(true);
    try {
      const response = await authenticatedFetch('/api/connector-hub/indexes');
      const data = await response.json();
      if (data.success) {
        setAvailableIndexes(data.indexes || []);
      }
    } catch (error) {
      console.error('Error loading indexes:', error);
    } finally {
      setLoadingIndexes(false);
    }
  };

  const loadAvailableContainers = async () => {
    setLoadingContainers(true);
    try {
      const response = await authenticatedFetch('/api/connector-hub/containers');
      const data = await response.json();
      if (data.success) {
        setAvailableContainers(data.containers || []);
      }
    } catch (error) {
      console.error('Error loading containers:', error);
    } finally {
      setLoadingContainers(false);
    }
  };

  const handleDiscovery = async () => {
    setDiscoveryLoading(true);
    setMessage(null);
    
    try {
      const response = await authenticatedFetch('/api/discovery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          connector_type: 'sharepoint_onprem',
          site_url: config.sourceUrls[0],
          username: config.username,
          password: config.password,
          domain: config.domain,
          crawl_subsites: config.crawlSubsites
        })
      });

      const result = await response.json();

      if (result.success) {
        setDiscoveryResult(result.discovery);
        setMessage({
          text: `Discovery completed! Found ${result.discovery.totalSites} sites with approximately ${result.discovery.estimatedDocuments} documents.`,
          type: MessageBarType.success
        });
      } else {
        setMessage({
          text: `Discovery failed: ${result.error}`,
          type: MessageBarType.error
        });
      }
    } catch (error) {
      console.error('Discovery error:', error);
      setMessage({
        text: `Discovery failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: MessageBarType.error
      });
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setMessage(null);

    // Determine if this is create or update
    const isEditing = !!editingJob?.jobId;

    try {
      // Debug logging - see what URLs we have
      console.log('🔍 DEBUG Frontend config.sourceUrls:', config.sourceUrls);
      console.log('🔍 DEBUG Filtered URLs:', config.sourceUrls.filter(url => url.trim()));
      
      // Prepare the job configuration
      const jobConfig = {
        name: config.name,
        description: config.description,
        connectorTemplate: 'sharepoint_onprem',
        targetIndex: config.selectedIndexMode === 'existing' ? config.selectedIndexName : config.newIndexName,
        createNewIndex: config.selectedIndexMode === 'new',
        configuration: {
          source_urls: config.sourceUrls.filter(url => url.trim()),
          processing_mode: config.processingMode,
          upload_only: config.uploadOnly,
          storage_container: config.storageContainer,
          max_items: config.advancedFiltering.maxItems,  // Add max_items at root level for orchestrator
          authentication: {
            username: config.username,
            // Only include password if it's provided (for editing, empty means keep existing)
            ...(config.password.trim() && { password: config.password }),
            domain: config.domain
          },
          crawl_config: {
            crawl_subsites: config.crawlSubsites,
            content_types: config.contentTypes,
            file_types: config.fileTypes,
            acl_enabled: config.aclEnabled,
            include_acl: config.aclEnabled,
            extract_acl: config.aclEnabled,
            crawl_mode: config.crawlMode,
            max_file_age_enabled: config.maxFileAgeEnabled,
            max_file_age_days: config.maxFileAgeDays,
            included_libraries: config.includedLibraries,
            excluded_libraries: config.excludedLibraries,
            filename_filters: config.filenameFilters,
            filename_filter_logic: config.filenameFilterLogic,
            // Advanced filtering configuration
            ...(config.advancedFiltering.enabled && {
              filename_filters: {
                contains: config.advancedFiltering.filenameFilters.contains,
                pattern: config.advancedFiltering.filenameFilters.patterns,
                starts_with: config.advancedFiltering.filenameFilters.startsWidth,
                exact: config.advancedFiltering.filenameFilters.exact
              },
              url_content_filters: {
                must_contain: config.advancedFiltering.urlContentFilters.mustContain,
                cannot_contain: config.advancedFiltering.urlContentFilters.cannotContain
              },
              regex_patterns: {
                include: config.advancedFiltering.regexPatterns.include,
                exclude: config.advancedFiltering.regexPatterns.exclude
              },
              depth_limit: config.advancedFiltering.depthLimit,
              max_file_size: config.advancedFiltering.maxFileSize,
              max_items: config.advancedFiltering.maxItems
            })
          },
          ai_config: {
            enable_vectorization: config.enableVectorization,
            enable_embeddings: config.enableEmbeddings
          }
        }
      };

      console.log('🔍 DEBUG Full jobConfig being sent:', JSON.stringify(jobConfig, null, 2));

      const endpoint = isEditing 
        ? `/api/connector-hub/jobs/${editingJob.jobId}`
        : '/api/connector-hub/jobs';
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await authenticatedFetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jobConfig)
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          text: `SharePoint On-Premises connector job "${config.name}" ${isEditing ? 'updated' : 'created'} successfully!`,
          type: MessageBarType.success
        });

        // Call onComplete with the final configuration
        onComplete({
          ...config,
          jobId: result.job_id || editingJob?.jobId,
          indexName: config.selectedIndexMode === 'existing' ? config.selectedIndexName : config.newIndexName,
          status: isEditing ? editingJob?.status || 'updated' : 'created'
        });
      } else {
        setMessage({
          text: `Failed to ${isEditing ? 'update' : 'create'} job: ${result.error}`,
          type: MessageBarType.error
        });
      }
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} SharePoint On-Premises job:`, error);
      setMessage({
        text: `Error ${isEditing ? 'updating' : 'creating'} job: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: MessageBarType.error
      });
    } finally {
      setLoading(false);
    }
  };

  // Index creation is handled automatically when job starts - no manual creation needed

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="large" style={{ fontWeight: 600 }}>
              Basic Configuration
            </Text>
            
            <TextField
              label="Connector Name"
              value={config.name}
              onChange={(_, newValue) => setConfig(prev => ({ ...prev, name: newValue || '' }))}
              placeholder="e.g., Corporate SharePoint OnPrem"
              required
              errorMessage={isSubmitAttempted ? validationErrors.name : undefined}
            />

            <TextField
              label="Description"
              value={config.description}
              onChange={(_, newValue) => setConfig(prev => ({ ...prev, description: newValue || '' }))}
              placeholder="Brief description of this connector"
              multiline
              rows={2}
            />

            <Stack tokens={{ childrenGap: 8 }}>
              <Label required>SharePoint Site URLs</Label>
              {config.sourceUrls.map((url, index) => (
                <Stack key={index} horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                  <TextField
                    placeholder="https://sharepoint.company.com/sites/sitename"
                    value={url}
                    onChange={(_, newValue) => updateUrl(index, newValue || '')}
                    style={{ flex: 1, minWidth: '400px' }}
                  />
                  {config.sourceUrls.length > 1 && (
                    <DefaultButton
                      text="Remove"
                      onClick={() => removeUrl(index)}
                    />
                  )}
                </Stack>
              ))}
              {isSubmitAttempted && validationErrors.sourceUrls && (
                <Text variant="small" style={{ color: 'red', marginTop: 4 }}>
                  {validationErrors.sourceUrls}
                </Text>
              )}
              <DefaultButton
                text="Add Another URL"
                onClick={addUrl}
                iconProps={{ iconName: 'Add' }}
              />
            </Stack>

            <Separator />
            
            <Text variant="medium" style={{ fontWeight: 600 }}>Authentication Credentials</Text>

            <TextField
              label="Domain"
              value={config.domain}
              onChange={(_, newValue) => setConfig(prev => ({ ...prev, domain: newValue || '' }))}
              placeholder="CORP or company.local"
              description="Windows domain name (optional for some authentication methods)"
            />

            <TextField
              label="Username"
              required
              value={config.username}
              onChange={(_, newValue) => setConfig(prev => ({ ...prev, username: newValue || '' }))}
              placeholder="domain\\username or username@domain.com"
              errorMessage={isSubmitAttempted ? validationErrors.username : undefined}
            />

            <TextField
              label="Password"
              type="password"
              required
              value={config.password}
              onChange={(_, newValue) => setConfig(prev => ({ ...prev, password: newValue || '' }))}
              placeholder={editingJob ? "Enter new password (leave blank to keep current)" : "Enter password"}
              canRevealPassword
              errorMessage={isSubmitAttempted ? validationErrors.password : undefined}
              description={editingJob ? "Current password is encrypted and secure. Enter a new password only if you want to change it." : undefined}
            />

            <MessageBar messageBarType={MessageBarType.info}>
              <Text variant="small">
                <strong>Security Note:</strong> Your credentials are encrypted and securely stored. 
                They are only used to authenticate with your SharePoint On-Premises environment.
              </Text>
            </MessageBar>
          </Stack>
        );

      case 2:
        return (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="large" style={{ fontWeight: 600 }}>
              Search Index Selection
            </Text>

            <Dropdown
              label="Index Selection Mode"
              selectedKey={config.selectedIndexMode}
              options={[
                { key: 'existing', text: 'Use Existing Index' },
                { key: 'new', text: 'Create New Index' }
              ]}
              onChange={(_, option) => {
                const mode = option?.key as 'existing' | 'new';
                setConfig(prev => ({ 
                  ...prev, 
                  selectedIndexMode: mode,
                  selectedIndexName: '',
                  newIndexName: mode === 'new' ? 
                    `sharepoint-onprem-${prev.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}` : ''
                }));
              }}
              required
              errorMessage={isSubmitAttempted ? validationErrors.selectedIndexMode : undefined}
            />

            {config.selectedIndexMode === 'existing' && (
              <>
                <Dropdown
                  label="Select Existing Index"
                  selectedKey={config.selectedIndexName}
                  options={availableIndexes.map(index => ({
                    key: index.name,
                    text: `${index.name} (${index.documentCount} docs)`
                  }))}
                  onChange={(_, option) => setConfig(prev => ({ ...prev, selectedIndexName: option?.key as string }))}
                  disabled={loadingIndexes}
                  placeholder={loadingIndexes ? "Loading indexes..." : "Select an index"}
                  required
                />
                {isSubmitAttempted && validationErrors.selectedIndexName && (
                  <Text variant="small" style={{ color: 'red', marginTop: 4 }}>
                    {validationErrors.selectedIndexName}
                  </Text>
                )}
              </>
            )}

            {config.selectedIndexMode === 'new' && (
              <Stack tokens={{ childrenGap: 12 }}>
                <TextField
                  label="New Index Name"
                  value={config.newIndexName}
                  onChange={(_, newValue) => {
                    // Simply use the index name as provided, like SharePoint Online
                    setConfig(prev => ({ 
                      ...prev, 
                      newIndexName: newValue || '',
                      // If we're in "new" container mode and don't have a custom storage container, 
                      // auto-populate it with the same name as the index
                      storageContainer: (prev.containerMode === 'new' && newValue && 
                                       (!prev.storageContainer || prev.storageContainer.startsWith('sp-onprem-'))) ?
                                       newValue : prev.storageContainer
                    }));
                  }}
                  placeholder="e.g., sharepoint-onprem-index"
                  required
                  errorMessage={isSubmitAttempted ? validationErrors.newIndexName : undefined}
                  description="Enter your desired index name (same as SharePoint Online behavior)"
                />
                <MessageBar messageBarType={MessageBarType.info}>
                  <Text variant="small">
                    The index will be created automatically when the job starts. No manual creation required.
                  </Text>
                </MessageBar>
              </Stack>
            )}
          </Stack>
        );

      case 3:
        return (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="large" style={{ fontWeight: 600 }}>
              Authentication & Connection
            </Text>
            
            <Text variant="medium" style={{ color: '#666' }}>
              Configure authentication method and test the connection to SharePoint
            </Text>

            <Dropdown
              label="Authentication Method"
              selectedKey="ntlm"
              options={[
                { key: 'ntlm', text: 'NTLM Authentication' },
                { key: 'kerberos', text: 'Kerberos Authentication (Coming Soon)', disabled: true },
                { key: 'forms', text: 'Forms Based Authentication (Coming Soon)', disabled: true }
              ]}
              onChange={(_, option) => {
                // For now, only NTLM is supported
              }}
            />

            <MessageBar messageBarType={MessageBarType.info}>
              <Text variant="small">
                <strong>NTLM Authentication:</strong> Most common for Windows-based SharePoint environments. 
                Works with domain credentials and is suitable for most on-premises SharePoint installations.
              </Text>
            </MessageBar>

            <Separator />

            <Text variant="medium" style={{ fontWeight: 600 }}>Connection Test</Text>

            <PrimaryButton
              text="Test Connection"
              onClick={() => {
                // TODO: Implement connection test
                setMessage({ 
                  text: 'Connection test functionality will be implemented in a future update.', 
                  type: MessageBarType.info 
                });
              }}
              disabled={!config.sourceUrls[0]?.trim() || !config.username.trim() || !config.password.trim()}
              iconProps={{ iconName: 'PlugConnected' }}
            />

            <MessageBar messageBarType={MessageBarType.success}>
              <Text variant="small">
                Ready to proceed with the provided credentials. The crawler will authenticate using NTLM 
                with the specified username and password.
              </Text>
            </MessageBar>

            <Toggle
              label="Crawl Subsites"
              checked={config.crawlSubsites}
              onChange={(_, checked) => setConfig(prev => ({ ...prev, crawlSubsites: !!checked }))}
              onText="Yes, include subsites"
              offText="No, main site only"
            />
          </Stack>
        );

      case 4:
        return (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="large" style={{ fontWeight: 600 }}>
              Content Types & Filtering
            </Text>

            <Text variant="medium" style={{ color: '#666' }}>
              Choose what types of content to include and configure filtering options
            </Text>

            <Dropdown
              label="Content Types to Crawl"
              multiSelect
              selectedKeys={config.contentTypes}
              options={contentTypeOptions}
              onChange={(_, option) => {
                if (option) {
                  const newContentTypes = option.selected 
                    ? [...config.contentTypes, option.key as string]
                    : config.contentTypes.filter(type => type !== option.key);
                  setConfig(prev => ({ ...prev, contentTypes: newContentTypes }));
                }
              }}
              placeholder="Select content types to include"
            />

            <Dropdown
              label="File Types to Index"
              multiSelect
              selectedKeys={config.fileTypes}
              options={fileTypeOptions}
              onChange={(_, option) => {
                if (option) {
                  const newFileTypes = option.selected 
                    ? [...config.fileTypes, option.key as string]
                    : config.fileTypes.filter(type => type !== option.key);
                  setConfig(prev => ({ ...prev, fileTypes: newFileTypes }));
                }
              }}
              placeholder="Select file types to index"
            />

            <Separator />

            <Text variant="medium" style={{ fontWeight: 600 }}>Library Filtering</Text>
            
            <TextField
              label="Include Libraries (comma-separated)"
              value={config.includedLibraries.join(', ')}
              onChange={(_, newValue) => {
                const libraries = newValue ? newValue.split(',').map(lib => lib.trim()).filter(lib => lib) : [];
                setConfig(prev => ({ ...prev, includedLibraries: libraries }));
              }}
              placeholder="Documents, Shared Documents, Site Assets"
              description="Specify which document libraries to include (leave empty to include all)"
            />
            
            <TextField
              label="Exclude Libraries (comma-separated)"
              value={config.excludedLibraries.join(', ')}
              onChange={(_, newValue) => {
                const libraries = newValue ? newValue.split(',').map(lib => lib.trim()).filter(lib => lib) : [];
                setConfig(prev => ({ ...prev, excludedLibraries: libraries }));
              }}
              placeholder="Form Templates, Style Library, _catalogs"
              description="Specify which document libraries to exclude from crawling"
            />

            <Separator />

            <Text variant="medium" style={{ fontWeight: 600 }}>File Filtering</Text>

            <Stack tokens={{ childrenGap: 8 }}>
              <Toggle
                label="Filter by File Age"
                checked={config.maxFileAgeEnabled}
                onChange={(_, checked) => setConfig(prev => ({ ...prev, maxFileAgeEnabled: !!checked }))}
                onText="Only index recent files"
                offText="Index all files regardless of age"
              />
              
              {config.maxFileAgeEnabled && (
                <TextField
                  label="Maximum File Age (days)"
                  type="number"
                  value={config.maxFileAgeDays.toString()}
                  onChange={(_, newValue) => {
                    const days = parseInt(newValue || '90');
                    setConfig(prev => ({ ...prev, maxFileAgeDays: isNaN(days) ? 90 : days }));
                  }}
                  description="Only files modified within this many days will be indexed"
                  style={{ maxWidth: '200px' }}
                />
              )}

              <TextField
                label="Maximum Items to Process"
                type="number"
                value={config.advancedFiltering.maxItems.toString()}
                onChange={(_, newValue) => {
                  const items = parseInt(newValue || '0');
                  setConfig(prev => ({
                    ...prev,
                    advancedFiltering: {
                      ...prev.advancedFiltering,
                      maxItems: isNaN(items) || items < 0 ? 0 : items
                    }
                  }));
                }}
                description="Maximum number of items to process (0 = no limit). Useful for testing or limiting processing load."
                placeholder="0 (no limit)"
                style={{ maxWidth: '200px' }}
              />
            </Stack>

            <MessageBar messageBarType={MessageBarType.info}>
              <Text variant="small">
                <strong>Content Filtering:</strong> Configure which SharePoint content to include in your search index. 
                You can filter by content type, file type, library, and file age to optimize crawling performance.
              </Text>
            </MessageBar>
          </Stack>
        );

      case 5:
        return (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="large" style={{ fontWeight: 600 }}>
              Security & AI Features
            </Text>

            <Text variant="medium" style={{ color: '#666' }}>
              Configure security settings and AI-powered content processing
            </Text>

            <Text variant="medium" style={{ fontWeight: 600 }}>Security Settings</Text>

            <Toggle
              label="Enable Access Control Lists (ACLs)"
              checked={config.aclEnabled}
              onChange={(_, checked) => setConfig(prev => ({ ...prev, aclEnabled: !!checked }))}
              onText="Security permissions preserved"
              offText="All content publicly searchable"
            />

            <MessageBar messageBarType={config.aclEnabled ? MessageBarType.success : MessageBarType.warning}>
              <Text variant="small">
                {config.aclEnabled ? (
                  <>
                    <strong>ACLs Enabled:</strong> SharePoint permissions will be preserved. Users will only see content 
                    they have access to in the original SharePoint environment.
                  </>
                ) : (
                  <>
                    <strong>ACLs Disabled:</strong> All indexed content will be searchable by all users. 
                    This may expose content that was previously restricted.
                  </>
                )}
              </Text>
            </MessageBar>

            <Separator />

            <Text variant="medium" style={{ fontWeight: 600 }}>AI Features</Text>

            <Toggle
              label="Enable Vectorization"
              checked={config.enableVectorization}
              onChange={(_, checked) => setConfig(prev => ({ ...prev, enableVectorization: !!checked }))}
              onText="AI-powered semantic search enabled"
              offText="Basic keyword search only"
            />

            <Toggle
              label="Enable Embeddings"
              checked={config.enableEmbeddings}
              onChange={(_, checked) => setConfig(prev => ({ ...prev, enableEmbeddings: !!checked }))}
              onText="Create document embeddings"
              offText="Skip embedding generation"
            />

            <MessageBar messageBarType={MessageBarType.info}>
              <Text variant="small">
                <strong>AI Features:</strong> Vector embeddings enable semantic search capabilities, allowing users 
                to find content based on meaning rather than just keywords. This improves search relevance and user experience.
              </Text>
            </MessageBar>

            <Separator />

            <Text variant="medium" style={{ fontWeight: 600 }}>Crawl Mode</Text>

            <Dropdown
              label="Crawl Mode"
              selectedKey={config.crawlMode}
              options={crawlModeOptions}
              onChange={(_, option) => setConfig(prev => ({ ...prev, crawlMode: option?.key as 'full' | 'delta' }))}
            />

            <MessageBar messageBarType={MessageBarType.info}>
              <Text variant="small">
                <strong>Crawl Modes:</strong> Full crawl indexes all content, while delta (incremental) crawl only 
                processes new and changed content since the last crawl. Delta crawl is more efficient for regular updates.
              </Text>
            </MessageBar>
          </Stack>
        );

      case 6:
        return (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="large" style={{ fontWeight: 600 }}>
              Scheduling & Processing
            </Text>

            <Text variant="medium" style={{ color: '#666' }}>
              Configure how and when your SharePoint content is crawled and processed
            </Text>

            <Dropdown
              label="Processing Mode"
              selectedKey={config.processingMode}
              onChange={(_, option) => {
                if (option) {
                  setConfig(prev => ({ 
                    ...prev, 
                    processingMode: option.key as 'realtime' | 'batch',
                    storageContainer: option.key === 'realtime' ? '' : prev.storageContainer,
                    // Force batch mode when upload_only is enabled
                    uploadOnly: option.key === 'realtime' ? false : prev.uploadOnly
                  }));
                }
              }}
              options={[
                { 
                  key: 'realtime', 
                  text: '⚡ Real-time Processing'
                },
                { 
                  key: 'batch', 
                  text: '📦 Batch Processing (Recommended for Large Libraries)'
                }
              ]}
            />

            {config.processingMode === 'batch' && (
              <Toggle
                label="Upload-only Mode"
                checked={config.uploadOnly}
                onChange={(_, checked) => setConfig(prev => ({ ...prev, uploadOnly: checked || false }))}
                onText="Upload Only"
                offText="Full Processing"
              />
            )}

            {config.uploadOnly && config.processingMode === 'batch' && (
              <MessageBar messageBarType={MessageBarType.warning}>
                <Text variant="small">
                  <strong>Upload-only Mode:</strong> Files will be crawled with metadata and ACL extraction, then uploaded to Azure Storage. 
                  No indexer or index will be created automatically - you'll need to configure skillset and indexer manually.
                  This is useful for special files that require custom processing.
                </Text>
              </MessageBar>
            )}

            {config.processingMode === 'realtime' && (
              <MessageBar messageBarType={MessageBarType.info}>
                <Text variant="small">
                  <strong>Real-time Processing:</strong> Documents are processed immediately during crawling. 
                  This mode works well for smaller SharePoint libraries but may hit rate limits with large volumes.
                </Text>
              </MessageBar>
            )}

            {config.processingMode === 'batch' && !config.uploadOnly && (
              <Stack tokens={{ childrenGap: 12 }}>
                <MessageBar messageBarType={MessageBarType.success}>
                  <Text variant="small">
                    <strong>Batch Processing:</strong> Files are copied to Azure Storage, then batch processed. 
                    This approach handles large SharePoint libraries efficiently and avoids rate limits.
                  </Text>
                </MessageBar>

                <Stack tokens={{ childrenGap: 8 }}>
                  <Label required>Storage Container</Label>
                  <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                    <Dropdown
                      placeholder="Select container mode"
                      options={[
                        { key: 'existing', text: 'Use existing container' },
                        { key: 'new', text: 'Create new container' }
                      ]}
                      selectedKey={config.containerMode}
                      onChange={(_, option) => {
                        if (option) {
                          const mode = option.key as 'existing' | 'new';
                          setConfig(prev => ({ 
                            ...prev, 
                            containerMode: mode,
                            storageContainer: mode === 'new' ? 
                              (prev.selectedIndexMode === 'new' && prev.newIndexName ? 
                                prev.newIndexName : 
                                `sp-onprem-${prev.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`) : ''
                          }));
                        }
                      }}
                      style={{ minWidth: 200 }}
                    />
                    
                    {config.containerMode === 'existing' ? (
                      <Dropdown
                        placeholder="Select existing container"
                        options={availableContainers.map(container => ({
                          key: container,
                          text: container
                        }))}
                        selectedKey={config.storageContainer}
                        onChange={(_, option) => {
                          if (option) {
                            setConfig(prev => ({ ...prev, storageContainer: option.key as string }));
                          }
                        }}
                        disabled={loadingContainers}
                        style={{ flex: 1, minWidth: 250 }}
                      />
                    ) : (
                      <TextField
                        placeholder="Enter new container name"
                        value={config.storageContainer}
                        onChange={(_, value) => setConfig(prev => ({ ...prev, storageContainer: value || '' }))}
                        style={{ flex: 1 }}
                      />
                    )}
                    
                    {loadingContainers && <Spinner size={SpinnerSize.small} />}
                  </Stack>
                  
                  {isSubmitAttempted && validationErrors.storageContainer && (
                    <Text variant="small" style={{ color: 'red' }}>
                      {validationErrors.storageContainer}
                    </Text>
                  )}
                </Stack>
              </Stack>
            )}

            <Separator />

            <Text variant="medium" style={{ fontWeight: 600 }}>Crawl Scheduling</Text>

            <Toggle
              label="Enable Scheduled Crawling"
              checked={config.schedulingEnabled || false}
              onChange={(_, checked) => setConfig(prev => ({ ...prev, schedulingEnabled: !!checked }))}
              onText="Automatic crawling enabled"
              offText="Manual crawling only"
            />

            {config.schedulingEnabled && (
              <Stack tokens={{ childrenGap: 12 }}>
                <Dropdown
                  label="Crawl Frequency"
                  selectedKey={config.crawlFrequency || 'daily'}
                  onChange={(_, option) => {
                    if (option) {
                      setConfig(prev => ({ ...prev, crawlFrequency: option.key as string }));
                    }
                  }}
                  options={[
                    { key: 'hourly', text: 'Every Hour' },
                    { key: 'daily', text: 'Daily' },
                    { key: 'weekly', text: 'Weekly' },
                    { key: 'monthly', text: 'Monthly' }
                  ]}
                />

                <TextField
                  label="Crawl Time (24-hour format)"
                  value={config.crawlTime || '02:00'}
                  onChange={(_, value) => setConfig(prev => ({ ...prev, crawlTime: value || '02:00' }))}
                  placeholder="14:30"
                  description="Time when scheduled crawls should start (e.g., 02:00 for 2 AM)"
                  style={{ maxWidth: '150px' }}
                />

                <Toggle
                  label="Incremental Crawling"
                  checked={config.incrementalCrawl !== false}
                  onChange={(_, checked) => setConfig(prev => ({ ...prev, incrementalCrawl: !!checked }))}
                  onText="Only crawl changed content"
                  offText="Full crawl every time"
                />

                <MessageBar messageBarType={MessageBarType.info}>
                  <Text variant="small">
                    <strong>Scheduling Tips:</strong> Use incremental crawling to improve performance by only processing 
                    content that has changed since the last crawl. Schedule crawls during off-peak hours to minimize 
                    impact on SharePoint performance.
                  </Text>
                </MessageBar>
              </Stack>
            )}

            <MessageBar messageBarType={MessageBarType.info}>
              <Text variant="small">
                <strong>Processing Tips:</strong> Batch processing is recommended for large SharePoint libraries 
                to avoid rate limits and ensure reliable processing. Real-time processing is suitable for smaller 
                sites or when immediate indexing is required.
              </Text>
            </MessageBar>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Panel
      isOpen={isVisible}
      onDismiss={onDismiss}
      type={PanelType.large}
      headerText={editingJob ? "Edit SharePoint On-Premises Connector Job" : "SharePoint On-Premises Connector Setup"}
      className={styles.setupPanel}
      isLightDismiss={false}
      hasCloseButton={true}
      onRenderNavigationContent={() => null}
    >
      <Stack tokens={{ childrenGap: 24 }}>
        {/* Progress indicator */}
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          {[1, 2, 3, 4, 5, 6].map((step) => (
            <div
              key={step}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: step <= currentStep ? '#0078d4' : '#e1dfdd',
                color: step <= currentStep ? 'white' : '#666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              {step}
            </div>
          ))}
        </Stack>

        {/* Step titles */}
        <Text variant="medium" style={{ fontWeight: 600 }}>
          Step {currentStep} of 6: {
            currentStep === 1 ? 'Basic Configuration' :
            currentStep === 2 ? 'Index Selection' :
            currentStep === 3 ? 'Authentication & Connection' :
            currentStep === 4 ? 'Content Types & Filtering' :
            currentStep === 5 ? 'Security & AI Features' : 'Scheduling & Processing'
          }
        </Text>

        {/* Message bar */}
        {message && (
          <MessageBar
            messageBarType={message.type}
            onDismiss={() => setMessage(null)}
          >
            {message.text}
          </MessageBar>
        )}

        {/* Step content */}
        {renderStepContent()}

        {/* Navigation buttons */}
        <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: 8 }}>
          <DefaultButton
            text="Previous"
            onClick={handlePrevStep}
            disabled={currentStep === 1 || loading}
          />
          
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            {currentStep < 6 ? (
              <PrimaryButton
                text="Next"
                onClick={handleNextStep}
                disabled={loading}
              />
            ) : (
              <PrimaryButton
                text={editingJob ? "Update Connector" : "Create Connector"}
                onClick={handleSubmit}
                disabled={loading}
              />
            )}
            
            <DefaultButton
              text="Cancel"
              onClick={onDismiss}
              disabled={loading}
            />
          </Stack>
        </Stack>

        {loading && (
          <Stack horizontalAlign="center" tokens={{ childrenGap: 8 }}>
            <Spinner size={SpinnerSize.medium} />
            <Text>{editingJob ? 'Updating' : 'Creating'} SharePoint On-Premises connector job...</Text>
          </Stack>
        )}
      </Stack>
    </Panel>
  );
};

export default SharePointOnPremConnectorSetup;
